import React from 'react';
import { Box, Dialog, DialogTitle, DialogContent, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { modalStyles } from '../../../styles/modalStyles';
import { WorkflowErrorBoundary } from './WorkflowErrorBoundary';
import WorkflowCanvasTab from './WorkflowCanvasTab';

interface WorkflowCanvasPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  channelId: string;
  channelBusinessRules?: string;
  /** PR #650 review (C1) — forwarded straight through to WorkflowCanvasTab/ChannelRuleFlowsView,
   * which persists node edits to the backend itself and fires this with the resulting text.
   * Without it, a caller's own copy of the business rules text (e.g. AddSpaceModal's local
   * state) goes stale the moment the canvas saves an edit here. */
  onBusinessRulesChanged?: (updatedText: string) => void;
}

/**
 * Onboarding-only popup preview of the workflow canvas (Odoo #17929 — "See Workflow" during
 * Add Channel). Wraps the same WorkflowCanvasTab/ChannelRuleFlowsView used post-onboarding in
 * ChannelSettings.tsx's 'workflow' tab, reusing BusinessRulesSection's rules-expand Dialog
 * styling (zIndex 9999, 90vh cap) since this renders on top of AddSpaceModal's own Dialog.
 * Closing has no effect on the onboarding stepper — it's a pure preview/edit surface; the
 * canvas persists its own edits via ChannelRuleFlowsView exactly as it does in Channel Settings.
 */
const WorkflowCanvasPreviewDialog: React.FC<WorkflowCanvasPreviewDialogProps> = ({
  open,
  onClose,
  channelId,
  channelBusinessRules,
  onBusinessRulesChanged,
}) => {
  // PR #650 review (LOW) — guarding via the Dialog's own `open` condition rather than an
  // early return before any hooks: this component has none today, but an early return ahead
  // of a future hook (e.g. a useEffect fetching canvas state) would violate the Rules of
  // Hooks. Functionally identical today (MUI's Dialog renders nothing when open is false).
  return (
    <Dialog
      open={open && !!channelId}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 3,
          height: '90vh',
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          color: 'text.primary',
        },
      }}
      sx={{ zIndex: 9999 }}
    >
      <DialogTitle sx={modalStyles.dialogTitle}>
        <Typography variant="h6" component="span">
          Workflow
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close workflow preview"
          sx={modalStyles.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
          <WorkflowErrorBoundary>
            <WorkflowCanvasTab
              channelId={channelId}
              channelBusinessRules={channelBusinessRules}
              onBusinessRulesChanged={onBusinessRulesChanged}
            />
          </WorkflowErrorBoundary>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default WorkflowCanvasPreviewDialog;
